/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { css, jsx, styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useCreateTeamDialog } from '@jira/issue__field-create-team-dialog/src/useCreateTeamDialog.tsx';
import type { Team } from '@jira/issue__types/src/Team.tsx';
import type { TeamValue } from '@jira/issue__types/src/TeamValue.types.tsx';
import { TeamPickerEdit } from '@jira/issue__field-team/src/TeamPickerEditEntryPointContent.tsx';
import { ScreenReaderText } from '@jira/platform__accessibility/src/common/ui/screenreader-text/index.tsx';
import EnterEscapeHandler from '@jira/platform__enter-escape-handler/src/index.tsx';
import ErrorBoundary from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import type { OnChange as SmartUserPickOnChange } from '@atlaskit/smart-user-picker';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	FieldValueSinglePickerProps,
	TeamFieldValue,
} from './BoardCreateFormValues.types.tsx';

const placeholder = defineMessage({
	id: 'software-board-create.modal.form.source-value-picker.team-picker.placeholder',
	defaultMessage: 'Select',
	description: 'Placeholder text for the team picker',
});

const TEAM_A11Y_LABEL_ID = 'software-board-create.source-value.team';

type TeamPickerProps = {
	label: string;
} & FieldValueSinglePickerProps<TeamFieldValue>;

export const TeamPicker = ({ onChange, value, isInvalid, label }: TeamPickerProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const handleOnCreate = useCallback(
		(team: TeamValue) => {
			if (!team) return;

			// wait for the team to be created before updating the value
			setTimeout(() => {
				onChange(team);
			}, 1000);
		},
		[onChange],
	);

	const [createTeamDialog, createTeamDialogTrigger] = useCreateTeamDialog({
		onCreate: handleOnCreate,
	});

	const handleChange: SmartUserPickOnChange = (team) => {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		onChange?.((team as Team) || null);
	};

	return (
		<TeamPickerEditWrapper>
			{/* Prevent ESC press on Select bubbling up to the modal */}
			<>
				<ScreenReaderText id={TEAM_A11Y_LABEL_ID}>{label}</ScreenReaderText>
				<EnterEscapeHandler onEscape={noop}>
					<TeamPickerEdit
						siteId={cloudId}
						isCompact={false}
						hasNoTeamOption={false}
						createTeamDialogTrigger={createTeamDialogTrigger}
						placeholder={formatMessage(placeholder)}
						onChange={handleChange}
						defaultValue={value}
						isDropdownMenuFixedAndLayered
						isInvalid={isInvalid}
						isClearable={false}
						aria-labelledby={TEAM_A11Y_LABEL_ID}
					/>
				</EnterEscapeHandler>
			</>
			{/* catch the error in the create team dialog */}
			<ErrorBoundary id="board-create-team-create-dialog">
				{/* Preventing the parent form from submitting */}
				<form
					css={removeFormSpaceStyle}
					tabIndex={-1}
					name="create-team-dialog-form"
					onSubmit={(e: React.FormEvent) => {
						e.stopPropagation();
					}}
				>
					{createTeamDialog}
				</form>
			</ErrorBoundary>
		</TeamPickerEditWrapper>
	);
};

const removeFormSpaceStyle = css({
	display: 'none',
});

// TODO: remove this after select compiled migration

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TeamPickerEditWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.fabric-user-picker__control': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		minHeight: '40px !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		height: '40px !important',
	},
});
